import {useMemo} from "react"
import {WatchQueryFetchPolicy} from "@irati/utils/apollo"
import {DataProxy, createUseQuery, UseQueryExtend} from "@irati/utils/services/apollo"
import useAuth from "@irati/auth/helpers/auth/useAuth"
import {BudgetCategories, PaymentCurrencyType, QueryWalletArgs, Wallet, WalletEntityType} from "@irati/types"
import {CurrencyType} from "@irati/payments/enums"
import {gql} from "@irati/utils/gql"
import walletErrorHandler from "../../services/errors/walletErrorHandler"
import budgetErrors from "../../helpers/errors/budgetErrors"
import {FullWalletFragment} from "../../graph/fragments/fullWallet"
import {Budgets} from "./shared"
import {useBudgetsPermissions} from "./useBudgetsPermissions"

export const WALLET_QUERY = gql`
  query WalletBudgets($userId: String!, $currency: String!, $entityId: ID, $entityType: WalletEntityType) {
    wallet(userId: $userId, entityId: $entityId, currency: $currency, entityType: $entityType) {
      ...FullWalletFragment
    }
  }

  ${FullWalletFragment}
`

const queryKey = "wallet.budgets"

/**
 * Abstraction over cache modification
 *
 * given that much of the cache manipulation is untyped, this allows a modfier
 * to specified in the same location as the query, whilst also preserving any
 * variables to ensure the cache key is constructed correctly
 *
 * want to experiment with this a little before lifting into the shared apollo tooling
 */
type CacheModifier<T, S extends Record<string, any>> = {
  /** an instance of the cache from mutation.update  */
  cache: DataProxy
  /** any variables for the cache read, if possible encapsulated as many as possible in the query file */
  variables: S
  /** a modify function that strongly types the cachedDate and ensures the correct value is updated upon write */
  modify(modifier: {cachedDate: T; write(updated: T): void}): void
}

/** Update apollo cache intended for use inside of a `useMutation` hook */
export function budgetsCacheModify({cache, variables: {uuid}, modify}: CacheModifier<Wallet, {uuid: string}>) {
  const cacheVars = {
    userId: uuid!,
    currency: PaymentCurrencyType.Rp,
    entityId: uuid!,
    entityType: WalletEntityType.Reward
  }

  const {wallet}: any = cache.readQuery({query: WALLET_QUERY, variables: cacheVars}) ?? {}

  if (!wallet) {
    console.warn("no wallet in cache, this really isn't expected")
    return
  }

  modify({
    cachedDate: wallet,
    write: (updated) =>
      cache.writeQuery({
        query: WALLET_QUERY,
        variables: cacheVars,
        data: {wallet: updated}
      })
  })
}

const useBudgetsQuery = createUseQuery<BudgetCategories, QueryWalletArgs>(WALLET_QUERY, {
  key: queryKey,
  errorHandlers: [walletErrorHandler, budgetErrors]
})

export type UseBudgets = UseQueryExtend<typeof useBudgetsQuery, {data: Budgets}>
export type UseBudgetsProps = {userId?: string; fetchPolicy?: WatchQueryFetchPolicy}

export function useBudgetsList(props?: UseBudgetsProps): UseBudgets {
  const {user} = useAuth()
  const uuid = props?.userId || user?.uuid
  const {canBudget} = useBudgetsPermissions()
  const {data: budgets, ...apollo} = useBudgetsQuery(
    {userId: uuid!, currency: CurrencyType.RP, entityId: uuid!, entityType: WalletEntityType.Reward},
    {skip: !canBudget, fetchPolicy: props?.fetchPolicy || "cache-first"}
  )

  const data = useMemo(() => {
    if (!budgets) {
      return undefined
    }

    const total = budgets.categories.reduce((a, b) => a + b.amount, 0) + budgets.unassigned.amount
    const hasNoBudgets = budgets.categories.length === 0

    return {
      total,
      hasNoBudgets,
      hasBudgets: !hasNoBudgets,
      budgets
    }
  }, [budgets])

  return {...apollo, data}
}
